// One rule for business display names, used by every screen and email.
// "Peec AI", "peec ai", "peec.ai", "trypeec.com" and a saved alias all
// collapse to one business, shown under one label.

import { entityLabel } from "./active-entities";

const norm = (s: string) => s.toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, " ").trim();
const SUFFIX = /\b(the|team|group|real estate|realty|realtors?|homes|properties|property|company|co|corp|inc|llc|ltd|associates|partners|and|of)\b/g;

/** Merge key for a business name or domain. Case-insensitive, suffix- and domain-insensitive. */
export function businessKey(name: string | null | undefined): string {
  let s = String(name ?? "").trim().toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "");
  const dom = s.match(/^([a-z0-9-]+)\.[a-z.]{2,}(\/.*)?$/);
  if (dom) s = dom[1].replace(/^(try|get|use|go|join|hey|meet)(?=[a-z0-9]{3,})/, "").replace(/(app|hq)$/, "") || dom[1];
  const k = norm(s).replace(SUFFIX, " ").replace(/\s+/g, " ").trim();
  const brand = k.replace(/\s?(ai|hq|app)$/, "").trim();
  const out = (brand.length > 2 ? brand : k) || norm(s);
  return out.replace(/\s+/g, "");
}

/** All keys a business answers to (name + aliases + domains). */
export function businessKeys(b: { name: string; aliases?: string[] | null; website_url?: string | null }): Set<string> {
  return new Set([b.name, ...(b.aliases ?? []), b.website_url ?? ""].map(businessKey).filter(Boolean));
}

/** Display label for a business name (domains become brand words). */
export function businessLabel(name: string | null | undefined): string {
  return entityLabel(name);
}

/**
 * Merge a list so each business appears once. The first item seen for a
 * business wins; later items whose name or alias shares a key are dropped.
 */
export function mergeBusinesses<T>(
  items: T[],
  nameOf: (t: T) => string,
  aliasesOf: (t: T) => (string | null | undefined)[] = () => [],
): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const it of items) {
    const keys = [nameOf(it), ...aliasesOf(it)].map((n) => businessKey(n)).filter(Boolean);
    if (!keys.length || keys.some((k) => seen.has(k))) continue;
    keys.forEach((k) => seen.add(k));
    out.push(it);
  }
  return out;
}

/** Unique display names, case- and alias-insensitive. */
export function uniqueBusinessNames(names: (string | null | undefined)[]): string[] {
  return mergeBusinesses(names.filter((n): n is string => !!n?.trim()), (n) => n).map(businessLabel);
}
